import React, { useEffect, useState } from "react";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import {
  ClearCache,
  GetCacheStats,
  RebuildRanks,
} from "../../api-requests/Updates";

interface CacheStats {
  total: number;
  ranks: number;
  lists: number;
  userLists: number;
  randomLists: number;
  other: number;
}

interface RebuildResult {
  category: string;
  categoryId: string;
  count: number;
}

type ActionStatus = "idle" | "loading" | "success" | "error";

interface ActionState {
  status: ActionStatus;
  message: string;
  results?: RebuildResult[];
}

const CacheAdmin = () => {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [clearState, setClearState] = useState<ActionState>({
    status: "idle",
    message: "",
  });
  const [rebuildState, setRebuildState] = useState<ActionState>({
    status: "idle",
    message: "",
  });

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await GetCacheStats(getBearerHeaders());
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to load cache stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleClearCache = async () => {
    if (
      !window.confirm(
        "Clear all caches? This will slow down the next few requests while caches are rebuilt.",
      )
    ) {
      return;
    }
    setClearState({ status: "loading", message: "" });
    try {
      const data = await ClearCache(getBearerHeaders());
      if (data.success) {
        setClearState({ status: "success", message: data.message });
        loadStats();
      } else {
        setClearState({
          status: "error",
          message: data.error || "Unknown error",
        });
      }
    } catch (err: any) {
      setClearState({
        status: "error",
        message: err?.response?.data?.error || "Request failed",
      });
    }
  };

  const handleRebuildRanks = async () => {
    setRebuildState({ status: "loading", message: "" });
    try {
      const data = await RebuildRanks(getBearerHeaders());
      if (data.success) {
        setRebuildState({
          status: "success",
          message: data.message,
          results: data.results,
        });
        loadStats();
      } else {
        setRebuildState({
          status: "error",
          message: data.error || "Unknown error",
        });
      }
    } catch (err: any) {
      setRebuildState({
        status: "error",
        message: err?.response?.data?.error || "Request failed",
      });
    }
  };

  const statRows = stats
    ? [
        { label: "Rank caches", value: stats.ranks, color: "text-blue-400" },
        { label: "List caches", value: stats.lists, color: "text-green-400" },
        {
          label: "User list caches",
          value: stats.userLists,
          color: "text-yellow-400",
        },
        {
          label: "Random list caches",
          value: stats.randomLists,
          color: "text-purple-400",
        },
        { label: "Other keys", value: stats.other, color: "text-gray-400" },
        { label: "Total keys", value: stats.total, color: "text-white" },
      ]
    : [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-8">
      <h2 className="text-2xl font-bold text-white">Cache &amp; Ranks</h2>

      {/* Cache Stats */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Cache Stats</h3>
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
          >
            {statsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {statRows.map((row) => (
              <div key={row.label} className="bg-gray-700 rounded p-3">
                <p className="text-xs text-gray-400">{row.label}</p>
                <p className={`text-2xl font-bold mt-1 ${row.color}`}>
                  {row.value}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">
            {statsLoading ? "Loading…" : "No data"}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Clear Cache */}
        <div className="bg-gray-800 rounded-lg p-6 flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Clear All Caches
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Flushes every Redis key. The next requests will be slower while
              caches warm up again. Use before deploying data changes.
            </p>
          </div>
          <button
            onClick={handleClearCache}
            disabled={clearState.status === "loading"}
            className="mt-auto px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium rounded-md text-sm transition-colors"
          >
            {clearState.status === "loading" ? "Clearing…" : "Clear Cache"}
          </button>
          {clearState.status === "success" && (
            <p className="text-green-400 text-sm">{clearState.message}</p>
          )}
          {clearState.status === "error" && (
            <p className="text-red-400 text-sm">{clearState.message}</p>
          )}
        </div>

        {/* Rebuild Ranks */}
        <div className="bg-gray-800 rounded-lg p-6 flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Rebuild Rankings
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Invalidates rank caches for all categories, recalculates scores
              from all lists, and updates the Items table. Also syncs the
              Community Top 10.
            </p>
          </div>
          <button
            onClick={handleRebuildRanks}
            disabled={rebuildState.status === "loading"}
            className="mt-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md text-sm transition-colors"
          >
            {rebuildState.status === "loading"
              ? "Rebuilding…"
              : "Rebuild Ranks"}
          </button>
          {rebuildState.status === "success" && (
            <div className="space-y-1">
              <p className="text-green-400 text-sm">{rebuildState.message}</p>
              {rebuildState.results && (
                <ul className="text-xs text-gray-300 space-y-0.5">
                  {rebuildState.results.map((r) => (
                    <li key={r.categoryId}>
                      {r.category}: {r.count} items ranked
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {rebuildState.status === "error" && (
            <p className="text-red-400 text-sm">{rebuildState.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CacheAdmin;
